import { Table, Tooltip } from 'antd';
import type { TableProps } from 'antd';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useStudyPrefetch } from '@/state/hooks/useStudyPrefetch';
import type { ReportListItem } from '@/models/report';
import type { Pagination } from '@/models';
import HeartLoader from '@/shared/ui/HeartLoader';
import ReportStatusChip from '../ReportStatusChip';
import ReportRowActions from './ReportRowActions';
import styles from './ReportTable.module.css';

interface ReportTableProps {
  data?: Pagination<ReportListItem>;
  loading?: boolean;
  page: number;
  pageSize: number;
  onTableChange: TableProps<ReportListItem>['onChange'];
}

export default function ReportTable({
  data,
  loading,
  page,
  pageSize,
  onTableChange,
}: ReportTableProps) {
  const navigate = useNavigate();
  const prefetchStudy = useStudyPrefetch();

  const columns: TableProps<ReportListItem>['columns'] = [
    {
      title: 'Patient',
      dataIndex: 'patientFirstName',
      key: 'patientFirstName',
      sorter: true,
      render: (_: string, record: ReportListItem) => {
        const hasAddendum = record.addendum !== null;
        const firstName = record.patientFirstName?.trim();
        const lastName = record.patientLastName?.trim();
        const fullName =
          firstName || lastName
            ? `${firstName ?? ''} ${lastName ?? ''}`.trim()
            : 'Unknown';
        const mrn = record.medicalRecordNumber?.trim();
        const cell = (
          <div className={styles.patientCell}>
            <span className={styles.patientName}>
              {fullName}
              {hasAddendum && <span className={styles.addendumDot} />}
            </span>
            <span className={styles.mrnLine}>MRN: {mrn || '—'}</span>
          </div>
        );

        if (!hasAddendum) return cell;
        return <Tooltip title="Addendum">{cell}</Tooltip>;
      },
    },
    {
      title: 'Modality',
      dataIndex: 'modality',
      key: 'modality',
      sorter: true,
      render: (text: string) => text?.trim() || '—',
    },
    {
      title: 'Site',
      dataIndex: 'site',
      key: 'site',
      sorter: true,
      render: (text: string) => text?.trim() || '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      render: (status: ReportListItem['status']) => (
        <ReportStatusChip status={status} />
      ),
    },
    {
      title: 'Signed By',
      dataIndex: 'signerLastName',
      key: 'signerLastName',
      sorter: true,
      render: (_: string, record: ReportListItem) => {
        if (!record.signerLastName) return '—';
        return `${record.signerFirstName ? `${record.signerFirstName} ` : ''}${record.signerLastName ?? ''}`;
      },
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      sorter: true,
      render: (text: string) => moment(text).format('MMM D, YYYY h:mm A'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: ReportListItem) => (
        <ReportRowActions record={record} />
      ),
    },
  ];

  return (
    <Table<ReportListItem>
      columns={columns}
      dataSource={data?.results}
      rowKey={(r) => r.uuid}
      loading={{ spinning: !!loading, indicator: <HeartLoader size="sm" /> }}
      onChange={onTableChange}
      bordered={false}
      rowClassName={() => styles.clickableRow}
      onRow={(record) => ({
        onClick: () => navigate(`/reports/${record.studyUuid}/preview`),
        onMouseEnter: () => prefetchStudy(record.studyUuid),
      })}
      pagination={{
        current: page,
        pageSize,
        total: data?.count,
        showSizeChanger: true,
        showTotal: (t, [s, e]) => `${s}-${e} of ${t}`,
      }}
    />
  );
}
